import React, { useState, useEffect } from "react";
import { Table, Button, Input, Modal, message } from "antd";
import { LeftOutlined, BulbOutlined, PlusOutlined } from "@ant-design/icons";
import { Edit, Trash2 } from "lucide-react";
import AddInventoryModal from "../../../../../modal/admin/addInventoryModal";
import EditInventoryModal from "../../../../../modal/admin/editInventoryModal";
import { getRequest, deleteRequest } from "../../../../../service/index"
import { getEmployeeData } from "../../../../../utils/localStorage";
import { formatDate } from "../../../../../utils/dateTime";

const Inventory = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const employeeData = getEmployeeData();
  const userRole = employeeData?.role;
  const inventoryPermissions = employeeData?.designation?.permissions?.Organization?.Inventory || [];

  const canView = userRole === "companyadmin" || inventoryPermissions.includes("view");
  const canAdd = userRole === "companyadmin" || inventoryPermissions.includes("add");
  const canEdit = userRole === "companyadmin" || inventoryPermissions.includes("edit");
  const canDelete = userRole === "companyadmin" || inventoryPermissions.includes("delete");

  const fetchInventory = async () => {
    setLoading(true);
    try {
      if (!canView) {
        setInventory([]);
      } else {
        const data = await getRequest("/api/inventory");
        setInventory(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      setInventory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const handleEdit = (record) => {
    if (!canEdit) {
      message.warning("You don't have permission to edit inventory.");
      return;
    }
    setSelectedInventory(record);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = (updated) => {
    setIsEditModalOpen(false);
    setSelectedInventory(null);
    if (updated) fetchInventory();
  };

  const handleDelete = (record) => {
    if (!canDelete) {
      message.warning("You don't have permission to delete inventory.");
      return;
    }
    Modal.confirm({
      title: "Are you sure you want to delete this inventory item?",
      content: `Product: ${record.productName}`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteRequest(`/api/inventory?id=${record._id}`);
          message.success("Inventory item deleted successfully");
          fetchInventory();
        } catch (error) {
          message.error("Failed to delete inventory item");
        }
      },
    });
  };

  const columns = [
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
      sorter: (a, b) => a.productName.localeCompare(b.productName),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      sorter: (a, b) => a.quantity - b.quantity,
      render: (_, record) => {
        const remaining = typeof record.remaining === "number" ? record.remaining : "-";
        const quantity = typeof record.quantity === "number" ? record.quantity : "-";
        return `${remaining}/${quantity}`;
      },
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      sorter: (a, b) => a.price - b.price,
      render: (price) => `${price.toLocaleString()}`,
    },
    {
      title: "Purchase Date",
      dataIndex: "purchaseDate",
      key: "purchaseDate",
      render: (date) => date ? formatDate(date) : "-",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<Edit className="w-4 h-4 text-blue-600" />}
            disabled={!canEdit}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            icon={<Trash2 className="w-4 h-4 text-red-600" />}
            disabled={!canDelete}
            onClick={() => handleDelete(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="px-4 pt-2 pb-4 flex flex-col gap-5 bg-white rounded-md ">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold mb-4">Inventory</h2>
        <div className="flex justify-between items-center gap-2">
          <Button icon={<LeftOutlined />}>Back</Button>
          <Button icon={<BulbOutlined />}>Page Tip</Button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 items-center mb-6">
        <Input.Search
          placeholder="Search inventory..."
          style={{ width: 300 }}
          className="mr-4"
        />

        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="bg-primary text-white"
          disabled={!canAdd}
          onClick={() => canAdd ? setIsModalOpen(true) : message.warning("You don't have permission to add inventory.")}
        >
          Add New Inventory
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={canView ? inventory : []}
        rowKey="_id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          total: (canView ? inventory.length : 0),
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        className="bg-white rounded-lg border border-gray-200"
      />

      <AddInventoryModal
        open={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
      />
      <EditInventoryModal
        open={isEditModalOpen}
        onRequestClose={handleEditModalClose}
        data={selectedInventory}
      />
      {!canView && (
        <div className="text-center text-gray-500 py-4">You don't have permission to view inventory.</div>
      )}
    </div>
  );
};

export default Inventory;
